import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  MenuItem,
  Grid,
  InputAdornment,
  Select,
  Box,
  IconButton,
  useTheme,
  Typography,
  FormControl,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  criteriaOptions,
  findStrategyById,
  hashGetBlacklistCritera,
  hashGetBlacklistType,
  operationOptions,
  unitOptions,
} from "../../../../Methods";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../../../../Redux/Reducers/uiSlice";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { sendWebSocketMessage } from "../../../../../hooks/useWebSocket";

const EditRuleDialog = ({
  open,
  blacklistData,
  strategies,
  editedRule,
  onClose,
  onSave,
  title,
}) => {
  const dispatch = useDispatch();
  const [strategy, setStrategy] = useState("All");
  const [tickers, setTickers] = useState([]);
  const [isRuleAdded, setIsRuleAdded] = useState(false);
  const [myrule, setMyrule] = useState({
    criteria: "",
    operation: "",
    value: "",
    unit: "",
  });
  const sectorOptions = [
    "Communication Services",
    "Consumer Discretionary",
    "Consumer Staples",
    "Energy",
    "Financials",
    "Health Care",
    "Industrials",
    "Information Technology",
    "Materials",
    "Real Estate",
    "Utilities",
  ];
  const assetOptions = [
    "ADR",
    "Common Stock",
    "Convertible Bond",
    "Corporate Bond",
    "ETF",
    "Future",
    "Government Bond",
    "Money Market",
    "Mutual Fund",
    "Option",
    "REIT",
    "Swap",
  ];

  const updatedStrategies = strategies ? [...strategies] : [];
  if (!updatedStrategies.some((strategy) => strategy.name === "All")) {
    updatedStrategies.unshift({ id: "all", name: "All" });
  }

  useEffect(() => {
    if (editedRule) {
      const { type, maximum, minimum, ticker, strategy, sectors, assetTypes } =
        editedRule;
      let operation = null;
      let value = null;

      if (minimum !== undefined && maximum !== undefined) {
        operation = "=";
        value = minimum;
      } else if (maximum !== undefined) {
        operation = ">";
        value = maximum;
      } else if (minimum !== undefined) {
        operation = "<";
        value = minimum;
      }

      setStrategy(editedRule?.strategy?.name || "All");
      setTickers(
        editedRule?.tickers?.length > 0 ? editedRule.tickers : ["All"]
      );
      setMyrule({
        criteria: hashGetBlacklistCritera(type),
        operation: type == 5 || type == 6 ? "=" : operation,
        value: type == 5 ? sectors : type == 6 ? assetTypes : value,
        unit: editedRule.unit || "",
      });
      setIsRuleAdded(true);
    } else {
      setStrategy("All");
      setTickers(["All"]);
      setMyrule({
        criteria: "",
        operation: "",
        value: "",
        unit: "",
      });
      setIsRuleAdded(false);
    }
  }, [editedRule, open]);

  useEffect(() => {
    if (!open) {
      setStrategy("All");
      setTickers(["All"]);
      setMyrule({
        criteria: "",
        operation: "",
        value: "",
        unit: "",
      });
      setIsRuleAdded(false);
    }
  }, [open]);

  const handleStrategyChange = (event) => {
    setStrategy(event.target.value);
  };

  const handleTickerChange = (event) => {
    const newTickers = event.target.value
      .split(",")
      .map((ticker) => ticker.trim());
    setTickers(newTickers);
  };
  const handleRuleChange = (field, value) => {
    if (field === "criteria") {
      if (value === "Sectors" || value === "Assets") {
        setMyrule((prevMyrule) => ({
          ...prevMyrule,
          operation: "=",
          value: [],
          [field]: value,
        }));
      } else {
        setMyrule((prevMyrule) => ({
          ...prevMyrule,
          [field]: value,
          operation: "",
          value: "",
        }));
      }
    } else {
      setMyrule((prevMyrule) => ({
        ...prevMyrule,
        [field]: value,
      }));
    }
  };

  const handleSave = () => {
    const { criteria, operation, value, unit } = myrule;
    const ruleObj = {
      type: hashGetBlacklistType(criteria),
    };
    const normalizedValue = Number(value);

    if (ruleObj.type === 5) {
      ruleObj.sectors = value;
    } else if (ruleObj.type === 6) {
      ruleObj.assetTypes = value;
    } else {
      if (operation === "<") {
        ruleObj.minimum = normalizedValue;
      } else if (operation === ">") {
        ruleObj.maximum = normalizedValue;
      } else if (operation === "=") {
        ruleObj.maximum = normalizedValue;
        ruleObj.minimum = normalizedValue;
      }
      //   ruleObj.unit = unit;
    }

    const updatedRule = tickers.includes("All")
      ? [
          {
            ...(strategy !== "All" && {
              strategy: findStrategyById(strategy, strategies),
            }),
            ...ruleObj,
          },
        ]
      : tickers.map((ticker) => ({
          ...(strategy !== "All" && {
            strategy: findStrategyById(strategy, strategies),
          }),
          ticker: ticker,
          ...ruleObj,
        }));
    const isDuplicate = blacklistData?.rules?.some((existingRule) =>
      updatedRule.some((newRule) => {
        if (existingRule.type !== newRule.type) return false;

        const existingStrategyId = existingRule.strategy?.strategyId || "All";
        const newStrategyId = newRule.strategy || "All";
        if (existingStrategyId !== newStrategyId) return false;

        const existingTickers = existingRule.tickers || [];
        const newTicker = newRule.ticker || "All";

        if (newTicker === "All") return true;

        return existingTickers.some(
          (ticker) => ticker.toLowerCase() === newTicker.toLowerCase()
        );
      })
    );

    if (isDuplicate && !editedRule) {
      toast.error("This rule already exists!");
      return;
    }

    const isModification =
      blacklistData?.rules && blacklistData.rules?.length > 0 ? true : false;

    const data = {
      action: isModification ? "modify_blacklist" : "create_blacklist_rule",
      data: {
        rules: isModification
          ? {
              add: [],
              modify: [],
              remove: [],
            }
          : [],
      },
    };

    if (isModification && editedRule) {
      data.data.rules.modify.push(...updatedRule);
      data.data.managerId = blacklistData.managerId;

      const previousTickers = editedRule.tickers || [];
      const removedTickers = previousTickers.filter(
        (ticker) => !tickers.includes(ticker)
      );
      if (previousTickers.length == 0) {
        data.data.rules.remove.push({
          ...(strategy !== "All" && {
            strategy: findStrategyById(strategy, strategies),
          }),
          ...ruleObj,
        });
      }
      removedTickers.forEach((ticker) => {
        data.data.rules.remove.push({
          ...(strategy !== "All" && {
            strategy: findStrategyById(strategy, strategies),
          }),
          ticker: ticker,
          ...ruleObj,
        });
      });
    } else if (isModification && !editedRule) {
      data.data.rules.add.push(...updatedRule);
      data.data.managerId = blacklistData.managerId;
    } else {
      data.data.rules.push(...updatedRule);
      data.data.manager = blacklistData.managerId;
      data.data.blacklistType = "manager";
    }
    sendWebSocketMessage(JSON.stringify(data));

    onClose();
    if(isModification){
      dispatch(addActiveRequests("modify_blacklist"));
    }else{
      dispatch(addActiveRequests("create_blacklist_rule"));
    }
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Updating Blacklist"));
  };
  const theme = useTheme();
  return (
    <Dialog
      fullWidth
      open={open}
      onClose={onClose}
      maxWidth="md"
      sx={{
        "& .css-1t1j96h-MuiPaper-root-MuiDialog-paper": {
          minWidth: "1000px",
          height: "100vh",
        },
      }}
    >
      <DialogTitle>
        <Typography
          variant="h6"
          component="div"
          align="center"
          style={{ flexGrow: 1, fontSize: "20px" }}
        >
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" sx={{ fontSize: "0.875rem" }}>
          Select Strategy
        </Typography>
        <FormControl fullWidth size="small" variant="outlined">
          <Select
            value={strategy}
            onChange={handleStrategyChange}
            name="strategy"
            sx={{ height: "50px" }}
          >
            {updatedStrategies?.map((strategyItem) => (
              <MenuItem key={strategyItem.id} value={strategyItem.name}>
                {strategyItem.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography
          variant="subtitle1"
          sx={{ marginTop: 2, fontSize: "0.875rem" }}
        >
          Enter Tickers (comma separated)
        </Typography>
        <TextField
          variant="outlined"
          fullWidth
          value={tickers}
          onChange={handleTickerChange}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <Typography
          variant="subtitle1"
          sx={{ marginTop: 2, fontSize: "0.875rem" }}
        >
          Criteria
        </Typography>

        <Grid container spacing={1} alignItems="center">
          <Grid item xs={4}>
            <Select
              fullWidth
              size="small"
              value={myrule?.criteria || ""}
              onChange={(e) => handleRuleChange("criteria", e.target.value)}
              displayEmpty
            >
              {criteriaOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={2}>
            <Select
              fullWidth
              size="small"
              value={myrule?.operation || ""}
              onChange={(e) => handleRuleChange("operation", e.target.value)}
              disabled={
                !myrule?.criteria ||
                myrule?.criteria === "NotAllowed" ||
                myrule?.criteria === "Sectors" ||
                myrule?.criteria === "Assets"
              }
            >
              {operationOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          {myrule.criteria === "Sectors" ? (
            <Grid item xs={6}>
              <Autocomplete
                multiple
                size="small"
                id="sectors-autocomplete"
                options={sectorOptions}
                value={myrule.value || []}
                fullWidth
                onChange={(event, newValue) =>
                  handleRuleChange("value", newValue)
                }
                disabled={!myrule?.criteria}
                renderInput={(params) => (
                  <TextField {...params} label="Select Sectors" />
                )}
              />
            </Grid>
          ) : myrule.criteria === "Assets" ? (
            <Grid item xs={6}>
              <Autocomplete
                multiple
                size="small"
                id="assets-autocomplete"
                options={assetOptions}
                value={myrule.value || []}
                fullWidth
                onChange={(event, newValue) =>
                  handleRuleChange("value", newValue)
                }
                disabled={!myrule?.criteria}
                renderInput={(params) => (
                  <TextField {...params} label="Select Assets" />
                )}
              />
            </Grid>
          ) : (
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                value={myrule.value}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    handleRuleChange("value", value);
                  }
                }}
                disabled={
                  !myrule?.criteria || myrule?.criteria === "NotAllowed"
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {myrule.unit}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          sx={{
            marginTop: "20px",
            float: "right",
            marginRight: "10px",
            marginBottom: "20px",
            padding: "8px 30px",
          }}
          onClick={handleSave}
          disabled={
            !myrule.criteria ||
            (myrule.criteria !== "NotAllowed" &&
              (!myrule.operation ||
                (myrule.criteria !== "Sectors" &&
                  myrule.criteria !== "Assets" &&
                  !myrule.value))) ||
            ((myrule.criteria === "Sectors" || myrule.criteria === "Assets") &&
              Array.isArray(myrule.value) &&
              myrule.value.length === 0)
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRuleDialog;
